import { useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { ChevronDown, Database, FileText, History, KeyRound, LogOut, Menu, MessageSquareText, Plus, Scale, Users, X } from 'lucide-react';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { auth } from '../api/endpoints';
import type { Sessao } from '../api/tipos';

function Marca() {
  return (
    <div className="flex items-center gap-2.5 px-4 py-4">
      <span aria-hidden className="h-7 w-1 rounded-sm bg-marca" />
      <div className="leading-tight">
        <p className="text-[15px] font-semibold">Precificador</p>
        <p className="text-[12px] text-tinta-2">Biopol Embalagens</p>
      </div>
    </div>
  );
}

function ItemMenu({ para, icone, children, fim }: { para: string; icone: ReactNode; children: ReactNode; fim?: boolean }) {
  return (
    <NavLink
      to={para}
      end={fim}
      className={({ isActive }) =>
        clsx(
          'relative flex h-9 items-center gap-2.5 rounded-campo px-3 transition-colors duration-150',
          isActive ? 'bg-acao-clara font-medium text-acao-escura' : 'text-tinta hover:bg-fundo',
        )
      }
    >
      {({ isActive }) => (
        <>
          {isActive && <span aria-hidden className="absolute top-1.5 bottom-1.5 left-0 w-[3px] rounded-r bg-acao" />}
          <span aria-hidden className="shrink-0 [&>svg]:size-4">
            {icone}
          </span>
          {children}
        </>
      )}
    </NavLink>
  );
}

function MenuLateral({ admin }: { admin: boolean }) {
  return (
    <nav aria-label="Menu principal" className="flex flex-col gap-0.5 px-2">
      <ItemMenu para="/rodadas/nova" icone={<Plus />}>
        Nova rodada
      </ItemMenu>
      <ItemMenu para="/rodadas" icone={<History />} fim>
        Rodadas
      </ItemMenu>
      <ItemMenu para="/custos" icone={<Scale />}>
        Custos
      </ItemMenu>
      <ItemMenu para="/pdf" icone={<FileText />}>
        PDF da tabela
      </ItemMenu>
      {admin && (
        <>
          <p className="mt-5 mb-1 px-3 text-[12px] font-medium text-tinta-2">Administração</p>
          <ItemMenu para="/admin/usuarios" icone={<Users />}>
            Usuários
          </ItemMenu>
          <ItemMenu para="/admin/conexoes" icone={<Database />}>
            Conexão com o CIGAM
          </ItemMenu>
          <ItemMenu para="/admin/mensagens" icone={<MessageSquareText />}>
            Mensagens do PDF
          </ItemMenu>
        </>
      )}
    </nav>
  );
}

function MenuUsuario({ sessao }: { sessao: Sessao }) {
  const [aberto, setAberto] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const navegar = useNavigate();
  const cliente = useQueryClient();

  useEffect(() => {
    if (!aberto) return;
    const fora = (e: MouseEvent) => !ref.current?.contains(e.target as Node) && setAberto(false);
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && setAberto(false);
    document.addEventListener('mousedown', fora);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', fora);
      document.removeEventListener('keydown', esc);
    };
  }, [aberto]);

  async function sair() {
    await auth.sair().catch(() => undefined);
    cliente.clear();
    navegar('/login', { replace: true });
  }

  const { usuario } = sessao;
  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={aberto}
        onClick={() => setAberto((v) => !v)}
        className="flex h-9 cursor-pointer items-center gap-2 rounded-campo px-2.5 hover:bg-fundo"
      >
        <span className="font-medium">{usuario.nome}</span>
        <span className="hidden text-tinta-2 sm:inline">{usuario.perfil === 'ADMIN' ? 'Administrador' : 'Usuário'}</span>
        <ChevronDown aria-hidden className="size-4 text-tinta-2" />
      </button>
      {aberto && (
        <div role="menu" className="absolute right-0 z-30 mt-1 w-52 rounded-painel border border-linha bg-superficie py-1 shadow-lg">
          <button
            role="menuitem"
            type="button"
            onClick={() => {
              setAberto(false);
              navegar('/trocar-senha');
            }}
            className="flex w-full cursor-pointer items-center gap-2.5 px-3 py-2 text-left hover:bg-fundo"
          >
            <KeyRound aria-hidden className="size-4 text-tinta-2" /> Trocar senha
          </button>
          <button
            role="menuitem"
            type="button"
            onClick={sair}
            className="flex w-full cursor-pointer items-center gap-2.5 px-3 py-2 text-left hover:bg-fundo"
          >
            <LogOut aria-hidden className="size-4 text-tinta-2" /> Sair
          </button>
        </div>
      )}
    </div>
  );
}

/** Selo do banco conectado, em todas as telas (SPEC §4.5): PRODUÇÃO em vermelho, HOMOLOGAÇÃO em amarelo. */
export function SeloAmbiente({ sessao }: { sessao: Sessao }) {
  const cigam = sessao.ambiente.cigam;
  if (!cigam) {
    return <span className="rounded-campo border border-linha px-2 py-0.5 text-[12px] font-semibold text-tinta-2">CIGAM NÃO CONFIGURADO</span>;
  }
  const producao = cigam.tipo === 'PRODUCAO';
  return (
    <span
      title={`${cigam.nome} — banco ${cigam.banco}`}
      className={clsx(
        'inline-flex items-center gap-1.5 rounded-campo px-2 py-0.5 text-[12px] font-semibold tracking-wide',
        producao ? 'bg-erro text-white' : 'bg-aviso-borda text-tinta',
      )}
    >
      <Database aria-hidden className="size-3.5" />
      {producao ? 'PRODUÇÃO' : 'HOMOLOGAÇÃO'}
      <span className="hidden font-normal opacity-90 md:inline">· {cigam.banco}</span>
    </span>
  );
}

/** Aviso permanente quando as publicações no CIGAM são simuladas. */
function FaixaSimulacao() {
  return (
    <div role="status" className="border-b border-aviso-borda/60 bg-aviso-fundo px-6 py-1.5 text-[13px] leading-[18px] text-aviso">
      <strong className="font-semibold">Modo simulação.</strong> As publicações são executadas no CIGAM e desfeitas em seguida: nenhum
      preço é gravado.
    </div>
  );
}

export function Layout({ sessao }: { sessao: Sessao }) {
  const [gavetaAberta, setGavetaAberta] = useState(false);
  const local = useLocation();
  const admin = sessao.usuario.perfil === 'ADMIN';

  useEffect(() => setGavetaAberta(false), [local.pathname]);

  return (
    <div className="flex h-dvh">
      {/* Menu fixo (≥ 1024 px) */}
      <aside className="hidden w-56 shrink-0 flex-col border-r border-linha bg-superficie lg:flex">
        <Marca />
        <MenuLateral admin={admin} />
      </aside>

      {/* Gaveta (< 1024 px) */}
      {gavetaAberta && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <button type="button" aria-label="Fechar menu" className="absolute inset-0 bg-tinta/40" onClick={() => setGavetaAberta(false)} />
          <aside className="relative flex h-full w-64 flex-col bg-superficie shadow-xl">
            <div className="flex items-center justify-between pr-2">
              <Marca />
              <button type="button" aria-label="Fechar menu" onClick={() => setGavetaAberta(false)} className="cursor-pointer rounded p-2 hover:bg-fundo">
                <X aria-hidden className="size-5" />
              </button>
            </div>
            <MenuLateral admin={admin} />
          </aside>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        {sessao.ambiente.modoEscrita === 'dryrun' && <FaixaSimulacao />}
        <header className="flex h-12 shrink-0 items-center justify-between gap-3 border-b border-linha bg-superficie px-4">
          <div className="flex items-center gap-2">
            <button
              type="button"
              aria-label="Abrir menu"
              onClick={() => setGavetaAberta(true)}
              className="cursor-pointer rounded p-2 hover:bg-fundo lg:hidden"
            >
              <Menu aria-hidden className="size-5" />
            </button>
            <SeloAmbiente sessao={sessao} />
          </div>
          <MenuUsuario sessao={sessao} />
        </header>
        <main id="conteudo" className="min-h-0 flex-1 overflow-y-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
